// THE FORM IN TWO LANGUAGES (TW-0082, 2026-09-08).
//
// Chris, 2026-09-07, mid-form-pass: "made me realize we also should add localization / support to
// switch the waiver form's language" · "Spanish and ads targeting it sounds smart." A client at the
// counter flips the form to Español and every word they read follows — labels, placeholders, the
// questions, the sentences they tick, the refusals, the errors, the done screen. The consent
// sentences themselves live in ONE file the API reads too (consent-sentences-es.json at the repo
// root; this app's copy is generated and checked byte-identical), because the PDF the shop keeps
// must print the sentence the client actually agreed to. service.ts carries the per-service words
// (titles, the artist/piercer block, the refusals) in both languages beside each other.
//
// THE LANGUAGE IS PER VISIT, NEVER REMEMBERED. It opens in the phone's own language (`?lang=` on
// the URL wins, for the terms link and for testing) and the switch overrides it for this page
// load only: a shop's tablet at the counter must not stick in Spanish after one Spanish-speaking
// client, and "Submit another waiver" reloads the page anyway. A shop's own default language is a
// later, shop-app setting (TW-0083). The one exception is the same client's own reload: since
// TW-0500 (2026-10-26) the draft in the tab (draft.ts) brings back the language they switched to
// with what they typed, until they sign or press "Start over", and never 30 minutes on.
//
// ENGLISH IS BYTE-FOR-BYTE WHAT THE FORM HAS SAID SINCE 2023. Every English string here was lifted
// from Form.tsx unchanged; `strings("en")` is the page as it was. The Spanish was written by a
// Fable session in plain tattoo-counter Spanish (tú, not usted) and is marked as awaiting a
// native-speaker read when one is affordable — never routed to Chris, who reads English only.

// TW-0227 — the one consent sentence in this table that the signed waiver quotes and that is not a clause or an optional
// box: the Spanish of "There is no second parent", read from the file the API prints it from, so the two cannot drift.
import consentEs from "./consent-sentences-es.json";
import type { IdType } from "./idType";
import { plural } from "./plural";

export type Lang = "en" | "es";
export const LANGS: Lang[] = ["en", "es"];

/** The flag beside each language on the switch — Chris's pick (2026-09-08: "yeah i prefer a Mexican flag
 *  in the US"). The files are public/flags/*.svg (its README says where they are from); the word stays
 *  beside the flag, because the word is the meaning and the flag is the cue. */
export const FLAG: Record<Lang, string> = { en: "us", es: "mx" };

/** The languages THIS shop offers: English always, and Spanish unless the owner turned it off in the
 *  shop app's Settings (Chris, 2026-09-22: "flip spanish support to always on, but turn-off-able").
 *  No list on `public_data.languages` is both; a list without "es" is the owner having unticked it —
 *  English only, no switch, a Spanish phone and a `?lang=es` both read English. From 2026-09-08 to
 *  2026-09-22 it was the other way round (Chris then: "hide the language picker on the default
 *  waiver form and make it a shop config that's off by default. Enabled in settings."). */
export function enabledLangs(publicData?: { languages?: unknown } | null): Lang[] {
  // 2026-09-22 — NO LIST AT ALL IS BOTH. Spanish is on for every shop unless its owner turns it off
  // (Chris: "flip spanish support to always on, but turn-off-able"), and the API's default is the same
  // (common.DEFAULT_LANGUAGES). A list without "es" is the owner having unticked it: English only, no
  // switch. Before that day absent meant English only (TW-0082, off by default).
  if (!publicData || !Array.isArray(publicData.languages)) return ["en", "es"];
  const offered = (publicData.languages as unknown[]).map((v) => `${v ?? ""}`.trim().toLowerCase());
  return offered.includes("es") ? ["en", "es"] : ["en"];
}

/** `?lang=es` / `?lang=en` on the URL, else the browser's own language, else English — and only
 *  ever a language in `enabled` (the shop's list, enabledLangs); anything else is English. */
export function detectLang(
  search: string = typeof window === "undefined" ? "" : window.location.search,
  browserLanguage: string = typeof navigator === "undefined" ? "" : navigator.language || "",
  enabled: Lang[] = LANGS
): Lang {
  const offered = (lang: Lang): Lang => (enabled.includes(lang) ? lang : "en");
  try {
    const asked = new URLSearchParams(search).get("lang");
    if (asked === "es" || asked === "en") return offered(asked);
  } catch {
    /* a malformed URL says nothing about the language */
  }
  return offered(`${browserLanguage}`.toLowerCase().startsWith("es") ? "es" : "en");
}

/** One noun or several, as a list: the demo's by-rule line names every service whose rule asks (TW-0224). */
const listOf = (works: string | string[]): string[] => (Array.isArray(works) ? works : [works]);

/** "a, b or c": the services a shop's client chooses between, named in one sentence (TW-0279: a shop's form can produce
 *  two or three kinds of waiver now). Two read "a or b", exactly the sentence a `both` shop has always shown. */
const orList = (works: string | string[], or: string): string => {
  const list = listOf(works);
  return list.length > 1 ? `${list.slice(0, -1).join(", ")} ${or} ${list[list.length - 1]}` : list.join("");
};

const EN = {
  // the switch itself, and the name of each language in that language
  languageName: { en: "English", es: "Español" } as Record<Lang, string>,
  switchLabel: "Language",

  // sections
  clientInformation: "Client Information",
  welcomeBack: "Welcome back",
  welcomeBackCheck: " — check that this is still right.",
  // TW-0500 (2026-10-26) — a reload put back what the client had typed (draft.ts), said in one line at the top of the form, and
  // the button beside it that empties the form instead
  draftKept: "We kept what you typed.",
  draftStartOver: "Start over",
  // TW-0195 — "Been here before?" at the top left, the mirror of the language switch at the top
  // right, and the short panel it opens. Drawn only where the shop turned the returning-client
  // lookup on (`public_data.returning_lookup`). The ANSWER is the welcomeBack line above on a
  // match and `lookupMissed` on every no — ONE line, the same line, whichever no it was: the API
  // answers a half match exactly as it answers a miss so that nobody can ask it "is this phone on
  // file at that shop?", and a second line here would give back what the API refuses to say.
  beenHereBefore: "Been here before?",
  lookupLabel: "Find your details from last time",
  lookupIntro: "Type the phone and email you used last time and we'll fill in the rest.",
  lookupFind: "Find me",
  lookupFinding: "Checking…",
  lookupClose: "Close",
  lookupMissed: "We did not find you. Fill in the form below.",
  // TW-0195, the demo only — drawn inside the panel on demo.waivers.tattoo and nowhere else. A
  // prospect has never signed at the demo shop, so the honest answer there is the miss line, which
  // demos nothing. This line says what the tap is about to show them.
  lookupDemoHint:
    "This is a demo. Tap \"Find me\" and the form fills in a made-up client, the way it would for one of yours.",
  questionnaire: "Pre-Procedure Questionnaire",
  acknowledgment: "Acknowledgment and Waiver",
  batchNumbers: "Needle and Ink Batch #s",
  // TW-0279 — a cosmetic tattoo's colour is its pigment (docs/COSMETIC.md "Lot numbers: needle and pigment"), and the
  // signed waiver says "Pigment Batch Number:" for it
  batchNumbersCosmetic: "Needle and Pigment Batch #s",
  whatToday: "What are you getting today?",

  // the client block
  name: "Name",
  firstName: "First Name",
  lastName: "Last Name",
  birthDate: "Birth Date",
  // TW-0503 (2026-10-29) — the age the page reads off the birth date, shown under it and never typed (birthDate.ts)
  youAre: (age: number) => `You are ${age}.`,
  mm: "MM",
  dd: "DD",
  yyyy: "YYYY",
  month: "Month",
  day: "Day",
  year: "Year",
  phone: "Phone Number",
  phonePlaceholder: "(000) 000-0000",
  email: "Email",
  emailPlaceholder: "you@example.com",
  // TW-0152 — the address they typed is one slip from a big provider (email-suggest.ts). An offer,
  // never a correction: tapping it takes it, typing on past it is always allowed. Split either side
  // of the address so the address itself renders underlined — the thing you get, marked as such,
  // the same way /start marks it. Spanish opens with ¿, which is why this is a pair and not a
  // prefix plus a fixed "?".
  didYouMeanBefore: "Did you mean ",
  didYouMeanAfter: "?",
  address: "Address",
  streetAddress: "Street address",
  addressLine2: "Apt, suite, unit (optional)",
  city: "City",
  state: "State",
  zip: "ZIP",
  // the address suggestions under the street box (TW-0084); Google's attribution line is not translated
  addressSuggestions: "Address suggestions",
  poweredByGoogle: "Powered by Google",

  // WHO TO CALL (2026-09-11). Asked when the shop turned it on, or when its state's rule requires it on
  // every client's record (South Carolina's and New Jersey's). Its own section under the client's
  // address, and all four are REQUIRED when it is asked: each is something a client can answer from
  // memory or their phone's contacts, so none of them is the kind of box Chris ruled out on 2026-09-10
  // ("We should never have it so if someone signs and fills out everything they can't finish"). Free
  // text throughout, so a city for an address you only half know is an answer.
  emergencySection: "Emergency Contact",
  emergencyWhy: (shop: string) => `Someone ${shop} can call if you need help.`,
  emergencyName: "Name",
  emergencyNamePlaceholder: "Their full name",
  emergencyRelationship: "Relationship",
  emergencyRelationshipPlaceholder: "ex: Mom, partner, friend",
  emergencyPhone: "Phone Number",
  emergencyAddress: "Address",
  emergencyAddressPlaceholder: "Street, city, state",
  needEmergencyName: "Enter your emergency contact's name.",
  needEmergencyRelationship: "Tell us how your emergency contact is related to you.",
  needEmergencyPhone: "Enter your emergency contact's phone number.",
  needEmergencyAddress: "Enter your emergency contact's address.",
  // THE CLIENT'S PHYSICIAN (2026-09-25, Squirtin' Ink): name, phone and address, asked after the health questions
  // where the shop turned it on for this service, and OPTIONAL: a walk-in with no doctor still signs.
  physicianSection: "Physician",
  physicianWhy: (shop: string) => `Your doctor, if you have one, so ${shop} can reach them if it needs to. You can leave this blank.`,
  physicianName: "Name",
  physicianNamePlaceholder: "Their full name",
  physicianPhone: "Phone Number",
  physicianAddress: "Address",
  physicianAddressPlaceholder: "Street, city, state",

  // the service block (the per-service words are service.ts's)
  todayIm: "Today I’m getting",
  aTattoo: "A tattoo",
  aPiercing: "A piercing",
  // TW-0279 — the third choice, for a shop whose services include cosmetic tattooing
  aCosmetic: "A cosmetic tattoo",
  // …and each service as a noun inside a sentence (tellUsService, chooseServiceForAcks)
  nounTattoo: "a tattoo",
  nounPiercing: "a piercing",
  nounCosmetic: "a cosmetic tattoo",
  chooseOne: "Choose one",
  // TW-0279 — WHAT A COSMETIC TATTOO IS TODAY (the proposal's section A, and "This visit is"): one or more procedures, as
  // boxes, because a visit is often brows and lips together, and what the visit is, as a list. Both are asked before the
  // signature and refused without an answer (Form.tsx): the procedures decide the questions and the clauses below them.
  cosmeticProcedures: "Procedures",
  cosmeticProceduresHint: "Tick every one you are getting today.",
  cosmeticVisit: "This visit is",
  needCosmeticProcedure: "Tick what you are getting today.",
  needCosmeticVisit: "Tell us what this visit is.",
  jewelryMaterial: "Jewelry Material",
  jewelryNotes: "Jewelry Notes",
  optional: "optional",
  jewelryNotesPlaceholder: "16g, 8mm flatback",
  jewelryNotesHint: "Gauge, size, brand — anything your piercer wants on the record",
  // TW-0154 — the price of the work, asked only by a shop that turned it on
  // (`public_data.ask_price`; Jose Velazquez, A Toda Madre Tattoos, 2026-09-10: "I would like a tab
  // for price of the tattoo as well included in the form … This would be both English or Spanish").
  // IT TAKES THE SERVICE, like `medications` above and like every word in service.ts: the key is
  // `ask_price` and not `ask_tattoo_price` precisely because a piercing studio can turn it on too.
  // Optional even when the shop asks — a client at the counter often does not know the number yet,
  // and a required money box on a consent form is a signature lost. Free text on purpose: "$150",
  // "150-200" and "TBD" are all real answers and none of them is worth refusing at a counter.
  // TW-0279 — THE SERVICE, never a yes/no: a boolean could only say piercing or not, and a cosmetic tattoo would have
  // read "Price of the Tattoo". Each service named on its own branch; "" (a `both` shop before the pick never draws it)
  // and a tattoo read the words they always have.
  priceLabel: (service: string) =>
    `Price of the ${service === "piercing" ? "Piercing" : service === "cosmetic" ? "Cosmetic Tattoo" : "Tattoo"}`,
  pricePlaceholder: "ex: $150",
  // 2026-09-11 — the price as a choice and a number, the default for a shop that asks (price.ts): Reckless Heart
  // Tattoo's three, in its front desk's own words ("1. Decided upon fee, 2. Estimate range, 3. Hourly rate"). The
  // kinds' words are also what the composed price says in parentheses, lowercased, so the two cannot disagree.
  priceKindFixed: "Decided upon fee",
  priceKindRange: "Estimate range",
  priceKindHourly: "Hourly rate",
  priceRangeJoin: "to",
  pricePerHour: "/hour",
  priceAmount: "Amount",
  priceFrom: "From",
  priceTo: "To",
  priceRate: "Rate per hour",
  // what the signed waiver will say, shown under the boxes before the client signs (S.C. Code Regs. 60-111 § 1003:
  // the client is "made aware of such charges ... as verified by his or her signature, prior to the procedure")
  priceOnWaiver: (price: string) => `On your waiver: ${price}`,
  // 2026-09-23 — how the client is paying, asked only by a shop that turned it on (`public_data.ask_payment_method`;
  // Cue at Piercinks, Miami: "is there a way to add a line for a form of payment? We usually have the client check off
  // if they are paying cash, credit card or other."). Three choices and no default, and REQUIRED once the shop asks:
  // the submit is refused without a pick (needPaymentMethod, below). "Card" and not "Credit card": a debit card is a
  // card too. The form posts the code (payment.ts); these are only what the client reads.
  paymentMethodQuestion: "How are you paying today?",
  paymentCash: "Cash",
  paymentCard: "Card",
  paymentOther: "Other",

  // the questionnaire
  yes: "Yes",
  no: "No",
  qInfluence: "Are you under the influence of drugs or alcohol?",
  qPregnant: "Are you pregnant or nursing?",
  qCommunicable: "Do you have a communicable disease?",
  qSkin: "Do you have any skin conditions?",
  // TW-0279 — the same question on a cosmetic form, in words that also ask about the skin where the work goes (the
  // proposal's B8 reuses `skinConditions`): everything the question above asks, and the site's own state. The signed
  // waiver prints it under the label it has always had.
  qSkinCosmetic:
    "Do you have any skin conditions, such as eczema, psoriasis or rosacea, or is the skin where you're getting this done sunburned, irritated, broken out or still healing?",
  communicableDetails: "Communicable disease (e.g. HIV, Hepatitis, etc.)",
  skinDetails: "Skin conditions (e.g. Rashes, eczema, infection, psoriasis, freckles, etc.)",
  ifYesIdentify: "If yes, please identify the condition.",
  tellYourPiercer: "Please tell your piercer the details.",
  // TW-0279 — under a cosmetic question's box: the artist, as the signed waiver names them
  tellYourArtist: "Please tell your artist the details.",
  medicalHistory:
    "Could you please provide details about your medical history? This might include conditions such as diabetes, cardiovascular disease, epilepsy or seizures, fainting, narcolepsy, blood-related diseases, immune disorders, keloid scarring, and so forth.",
  // TW-0279 — THE SERVICE, never a yes/no (priceLabel above has why). A cosmetic tattoo's is asked on EVERY cosmetic form
  // (the proposal's B3, which the API prints on every cosmetic record) and names the last day's aspirin, ibuprofen and
  // naproxen as well; it never says to stop anything. "" is a `both` shop before the pick, which reads as it always has.
  medications: (service: string) =>
    service === "cosmetic"
      ? "Do you take a blood thinner or any other medicine that might affect the cosmetic tattoo or healing, or have you taken aspirin, ibuprofen or naproxen in the last 24 hours? Please provide details."
      : `Do you currently take any medications, including blood thinners, that might affect the ${service === "piercing" ? "piercing" : "tattooing process"} or healing? Please provide details.`,

  // the acknowledgment block
  // the state's own questions (TW-0104, 2026-09-08): asked when the shop's state requires them
  qAllergies: "Are you allergic or sensitive to any medicines, ointments, soaps, latex or anything put on your skin?",
  // TW-0279 — the same question on a cosmetic form, which asks it in every state (the proposal's B10 reuses `allergies`,
  // "with these named"): everything the state's question asks, and the pigment, the metals and the numbing medicine
  qAllergiesCosmetic:
    "Are you allergic or sensitive to any medicines, ointments, soaps, latex or anything put on your skin, including tattoo ink or pigment, metals like nickel, or numbing medicines like lidocaine?",
  allergiesDetailsLabel: "Allergies or sensitivities",
  qBleeding: "Do you have a bleeding disorder, or a history of one? (Hemophilia, easy bruising, bleeding that takes a long time to stop.)",
  bleedingDetailsLabel: "Bleeding disorder",
  // 2026-09-17 — Georgia's client evaluation (r. 511-3-8-.08(3)(k)): a yes or no with nothing to explain
  qEaten: "Have you eaten in the last 4 hours?",
  // 2026-10-08 (TW-0279 slice 8) — Michigan's numbing rule (Mich. Admin. Code R 333.13109(5); R 333.13113(8), (9)): no work on
  // skin a numbing product has touched unless a doctor prescribed it, the prescription kept with the record. Asked on every
  // signing where the state's record names it (`numbing_prescription`), a yes or a no; a yes opens no box, it offers an
  // uploader for a photo of the prescription (below), OPTIONAL since the same evening: the client signs without it, and the
  // shop adds it to the record from its dashboard before the artist signs. `state` is the shop's state, by name.
  qNumbing: "Is there a numbing cream, spray or other numbing product on your skin, or will one be used today?",
  numbingPrescriptionPhoto: "Photo of the doctor's prescription for the numbing product",
  numbingPrescriptionNote: (state: string) =>
    `${state} doesn't allow the work on skin with a numbing product on it unless a doctor prescribed it, and the prescription is kept with your record. No prescription? Tell your artist before you sign.`,
  // 2026-10-16 (TW-0270 slice 4) — the five a state's rule can list as its own questions (the all-states audit's M04), each asked
  // where the shop's state names it, a yes or a no with no box. The three conditions ask the history, as the rules do ("have or
  // have had"); the two in a window ask the rule's window, which is the same in every state that names one.
  qDiabetesHistory: "Have you ever had diabetes?",
  qSeizuresOrFainting: "Have you ever had epilepsy, seizures, fainting spells or narcolepsy?",
  qHeartOrBloodPressure: "Have you ever had a heart condition, heart disease or high blood pressure?",
  qBloodThinnersRecently:
    "In the last 24 hours, have you taken a blood thinner, or aspirin, ibuprofen, naproxen or another anti-inflammatory?",
  qPregnantRecently: "Have you been pregnant in the last 3 months?",
  // the demo shop's state picker: the same form, with what that state asks for
  demoPickState: "Show the form for",
  demoStandard: "Any state",
  demoStateNote: "Some states ask for more on the record. Pick one to see its form.",
  demoPickType: "Shop does",
  demoTypeTattoo: "Tattoos only",
  demoTypeBoth: "Tattoos and piercings",
  demoTypePiercing: "Piercings only",
  // TW-0279 — a link's dial (`?type=cosmetic`), offered in the list only while a link carries it: the cosmetic form ships
  // dark until a shop can say it does cosmetic work (docs/COSMETIC.md's build order)
  demoTypeCosmetic: "Cosmetic tattoos only",
  demoPierceMinors: "We pierce minors with a parent present",
  // TW-0135 — the sentence under the dials. One line per thing that is not where it started, and
  // silence when nothing is: the plainest demo says nothing about itself.
  demoStateStandard: (state: string) =>
    `${state} uses the standard form — the same questions as every other state.`,
  demoTypeBothMeans: "Your client picks tattoo or piercing when they sign, and the form follows it.",
  demoTypePiercingMeans: "The form asks about the piercing, the jewelry and metal allergies.",
  demoTypeCosmeticMeans:
    "The form asks which procedures and what this visit is, asks the health questions those procedures bring, and shows a cosmetic tattoo's own acknowledgments.",
  demoMinorsAllowed: (state: string, work: string) =>
    `A client under 18 getting a ${work} gets a parent or guardian section, and the signed waiver prints what ${state} requires.`,
  // 2026-11-03 — a state that sets no rule for it (minors.ts stateSetsNoRule): what prints is the shop's own rule
  demoMinorsShopRule: (state: string, work: string) =>
    `A client under 18 getting a ${work} gets a parent or guardian section, and the signed waiver prints your shop's own rule: ${state} sets none for a ${work}.`,
  demoMinorsRefused: (state: string, work: string) =>
    `${state} does not allow a ${work} under 18, so the form still takes 18+ for that — and says why.`,
  // TW-0279 — a cosmetic tattoo takes no minor in any state, in this form's first cut: the shop's switch does not reach it
  demoMinorsCosmetic: "A client under 18 cannot sign for a cosmetic tattoo: the form takes 18+ for it in every state.",
  demoWorkTattoo: "tattoo",
  demoWorkPiercing: "piercing",
  demoWorkCosmetic: "cosmetic tattoo",
  demoMinorsPickState: "Pick a state above: the answer depends on the state.",
  // 2026-09-24 — the fourth dial, under the minors switch: the birth certificate or guardianship papers.
  demoAskProof: "Ask for the birth certificate or guardianship papers",
  // 2026-09-24 — the door's one-line summary of the dials that are set, and the modal's Done
  demoMinorsShort: "Under 18 with a parent",
  demoProofShort: "Birth certificate or guardianship papers",
  demoDone: "Done",
  demoProofOn: "When a parent or guardian signs, the form asks for a photo of the birth certificate or guardianship papers too.",
  // TW-0224 — `works` is every service whose rule names the proof (the piercing rule's own since 2026-09-25), so a
  // "both" demo in Michigan says "for a tattoo or a piercing" rather than joining two nouns with a slash. One
  // noun alone is taken as a list of one.
  demoProofByRule: (state: string, works: string | string[]) =>
    `${state} asks for a photo of the birth certificate or guardianship papers for ${listOf(works).map((w) => `a ${w}`).join(" or ")} on its own.`,
  // TW-0227 — a link's place (`?county=Lake%20County&city=Hammond`) whose rule is its own: Hammond, Baltimore City, Clark County
  demoCountyRule: (place: string, state: string, works: string | string[]) =>
    `${place} has a rule of its own for ${listOf(works).map((w) => `a ${w}`).join(" or ")} under 18, and the form follows it as well as ${state}'s.`,
  // TW-0227 step 3 — …and where that rule wants both parents, what the parent who is there is asked, the three ways (or
  // the two, where both have to be here) in the words the form's own options use
  demoBothParents: (place: string) =>
    `${place} asks for written permission from both parents, so the parent who is there picks one of three: the other parent signs here, a photo of the permission they signed, or there is no second parent.`,
  demoBothParentsPresent: (place: string) =>
    `${place} asks for both parents to be here, so the parent who is there picks one of two: the other parent signs here, or there is no second parent.`,
  // TW-0225 — the fifth dial, under the minors switch: the demo client is here for an ear piercing (its spot starts at
  // Earlobe), and the line says what the picked state's rule does with one
  demoEar: "Show a minor's ear piercing",
  demoEarShort: "Ear piercing",
  demoEarAsks: (state: string, question: string) =>
    `${state} writes some ear piercings out of its rule for a minor's piercing, so the form asks: "${question}" A yes asks for the parent's consent and nothing the rule names.`,
  demoEarCovered: (state: string) =>
    `${state}'s rule for a minor's piercing reaches an ear like any other spot, so an ear piercing is asked for what any piercing is.`,
  // the same thing, short, for beside the switch it disables — the sentence above is the one that
  // explains, and it is already on the row's tooltip and in the help block
  demoNeedsState: "Pick a state first",
  // only ever shown when a dial is off its default, which is exactly when it is worth showing
  demoReset: "Reset",
  // the demo's own chrome
  // NAMED BY WHAT IS BEHIND IT, not by the verb (Chris: "should Customize be more about 'Add
  // piercing customizations' or something so it's clearer and more explicit?"). "Customize this
  // demo" said nothing about which knobs. Not "piercing" though — the panel is also where a shop
  // says it does TATTOOS ONLY, and a door labelled piercing would be the one place a tattoo shop
  // never looks. The state has its own label in the bar and stays out of this.
  //
  // A NOUN RATHER THAN "Change what…" FOR ONE MEASURED REASON: with the chevron it is 176px, and a
  // 390px phone has 358px of bar minus a 124px wordmark. The verb form measured 233 and wrapped the
  // button onto its own line, which is exactly the top-right corner he asked it to sit in.
  demoCustomize: "What this shop does",
  demoShowQr: "Show QR code to scan with your phone",
  demoQrTitle: "Scan to open this demo on your phone",
  demoQrNote: "Everything you have set here comes with it.",
  demoQrClose: "Close",
  // TW-0136 — what the laptop says once the phone that scanned its code has opened it. The heading is Chris's own
  // sentence from the card. The line under it says what the form IS, never what a client will feel or do.
  demoOnPhoneTitle: "You're completing this on your phone",
  demoOnPhoneBody: "Your clients get this same form on their own phone, from your QR code.",
  demoOnPhoneBack: "Fill it in here instead",
  // TW-0206 — the choice the printed QR code opens first, for a shop whose owner turned it on and whose deposit page is
  // taking deposits (QrPicker.tsx). The deposit page's own button for the other direction says "Sign your waiver" too.
  pickerWaiver: "Sign your waiver",
  pickerDeposit: "Pay a deposit",
  // TW-0138 — the demo's chrome off the form route, where the dials would do nothing. "the waiver"
  // rather than "the form": it is the word a shop owner uses for the thing on the counter, and it
  // is the word the rest of this product uses everywhere a shop can read it.
  demoBackToWaiver: "Back to the waiver",
  demoStateAdds: (state: string) => `${state} adds`,
  changeAllergies: "an allergies question",
  changeBleeding: "a bleeding-disorder question",
  changeMedications: "the medications question",
  changeLots: "needle and ink lot-number photos",
  changeLicense: "the artist's license number",
  changeEmergencyContact: "an emergency contact",
  // THE TEN THAT WERE MISSING (2026-09-21). Ten ids joined the contract after this list was written and the
  // sentence fell back to the id itself, so the live demo read "California adds: risk_disclosure_tattoo,
  // aftercare_suggestions, …" in 13 states. demo.test.ts now holds this list to the contract.
  changeEatenRecently: "a question about eating in the last 4 hours",
  changeBloodDonor: "the blood-donor notice",
  changeGrievance: "how to file a complaint with the state",
  changeHealthAuthority: "how to reach the county board of health",
  changeAftercareSuggestions: "the aftercare suggestions in writing",
  changeStateDisclosure: "the state's own disclosure statement",
  // TW-0627 (batch 2, 2026-11-04) — Illinois's 797.700 request for the client's conditions, in the rule's words
  changeDisclosureRequest: "the state's own request that you disclose your conditions",
  changeRiskTattoo: "the tattoo risks the state lists",
  changeRiskPiercing: "the piercing risks the state lists",
  changeCautionaryNotice: "a cautionary notice the client ticks",
  changeConsentStatements: "the consent statements the state lists",
  // 2026-09-22 — West Virginia's, the eleventh: § 16-38-3(a)(4) has the patron acknowledge IN WRITING that they
  // received the Department of Health's information on the possible complications of a tattoo
  changeComplicationsInformation: "a box for the state's information on complications",
  // 2026-09-23 — Minnesota's, the twelfth: § 146B.07, subd. 1(c) has the form say the work will not be done
  // unless the health questions are completed and signed, and may be declined over a health condition
  changeDeclineStatement: "the statement that the work can be declined",
  // 2026-10-08 (TW-0279 slice 8) — Michigan's, the thirteenth: R 333.13109(5) keeps the doctor's prescription for a numbing
  // product with the record
  changeNumbingPrescription: "a numbing-product question, and a photo of the prescription on a yes",
  // 2026-10-16 (TW-0270 slice 4) — the five a state's rule can list as its own questions
  changeDiabetesHistory: "a diabetes question",
  changeSeizuresOrFainting: "a question about epilepsy, seizures or fainting",
  changeHeartOrBloodPressure: "a question about heart conditions and high blood pressure",
  changeBloodThinnersRecently: "a question about blood thinners and anti-inflammatories in the last 24 hours",
  changePregnantRecently: "a question about pregnancy in the last 3 months",
  // 2026-10-28 (TW-0526, the audit's M03) — Colorado's piercing sheet on the consent, Ohio's how-it-is-done, Louisiana's inks
  changeAftercareSuggestionsPiercing: "the piercing aftercare suggestions in writing",
  changeProcedureExplanationTattoo: "a written explanation of how the tattoo is done",
  changeProcedureExplanationPiercing: "a written explanation of how the piercing is done",
  changeInkRegulatoryStatus: "a prominent statement on whether the inks are approved",
  // TW-0526 (the audit's M12) — the box under the aftercare sheet
  changeAftercareReceived: "a box saying the aftercare instructions were received",
  license: "license",

  // TW-0279 — the choices the shop's form offers, named: two read "a tattoo or a piercing", the sentence a `both` shop
  // has always shown; a shop whose services include cosmetic tattooing names that too
  chooseServiceForAcks: (works: string | string[]) =>
    `Choose ${orList(works, "or")} above, and what you are agreeing to shows up here.`,
  // TW-0113 (2026-09-08) — THE TERMS BOX IS JUST THE TERMS, AND HEARING FROM THE SHOP IS ITS OWN
  // OPTIONAL BOX. Until today one required checkbox said "I accept the terms and conditions,
  // including hearing from <shop> by text, email or social media…", so a client could not sign
  // without agreeing to be marketed to — consent as a condition of service, which is not the
  // prior-express-written-consent the TCPA asks for and is the first thing an A2P 10DLC reviewer
  // looks for. The two are split: `acceptTermsEnd` closes the required sentence, and
  // `hearFromShop` is a second box, unticked, that the form never gates the signature on.
  acceptTerms: "I accept the ",
  termsLink: "terms and conditions",
  acceptTermsEnd: ".",
  // TWO SENTENCES, AND THE SPLIT BETWEEN THEM IS A CARRIER RULE, NOT A STYLE CHOICE. Twilio's
  // pre-submit check refused a single box on 2026-09-08: "Your opt-in bundles marketing consent
  // together with other (transactional/informational) consent in one statement. Capture them
  // separately — use a distinct, optional checkbox for promotional/marketing texts so consumers can
  // agree to each independently." So the service follow-up and the promotional one are separate
  // boxes a client ticks independently, and neither gates the signature.
  // Both are quoted verbatim by the campaign registration — change them here and they change there.
  // The API prints each on the signed PDF only when it was ticked (app.py).
  // "after today" since 2026-09-10: the terms always said the two boxes are for AFTER today — the
  // one email that goes out the moment a waiver lands, the shop's thank-you, is the "about today"
  // the terms cover (services/waiver-api/consolidated_api/client_thank_you.py). A clarification of
  // what the tick always meant, so consent_from reads it exactly as before and no epoch moves.
  hearFromShop: (shop: string) =>
    `Yes, ${shop} can text or email me after today about my aftercare and how my healing is going. Optional. Msg & data rates may apply; reply STOP any time.`,
  shopMarketing: (shop: string) =>
    `Yes, ${shop} can text or email me its news, offers and events. Optional. Msg & data rates may apply; reply STOP any time.`,
  // THE POLICY LINKS SIT AT THE POINT OF CONSENT, AND THAT PLACEMENT IS THE FIX FOR THREE A2P
  // REJECTIONS (TW-0111, 2026-09-08). All three came back 30908 on MESSAGE_FLOW — "a compliant
  // privacy policy can not be verified" — while the privacy page itself was live, current and
  // correct each time. The page was never the problem: the ROUTE to it was. This page, where a
  // client actually gives consent, linked its consent page and nothing else; the word "privacy"
  // appeared on it zero times, so a reviewer following the message flow could not get from the
  // opt-in to a privacy policy. The verified control on the same Twilio brand — funeral.link's
  // campaign — passes with a flow that says in as many words: "Directly under the checkbox the page
  // links our Privacy Policy and Terms of Use." These are those links, and they point at the two
  // URLs filed on the campaign itself (PrivacyPolicyUrl, TermsAndConditionsUrl), not at the shop's
  // own consent page, so what a reviewer clicks is what the filing names.
  policiesLead: "Texts and emails are sent by waivers.tattoo on the shop's behalf. ",
  privacyLink: "Privacy Policy",
  policiesJoin: " · ",
  siteTermsLink: "Terms",

  // A MINOR'S PIERCING WITH A PARENT PRESENT (TW-0128). Shown only where all three gates open: the
  // shop turned it on, the state hands the parent a route, and this placement is one that state
  // allows. Everywhere else the form says exactly what it has said since 2023 — see
  // minors.ts, which decides, and the API, which decides again on the posted body.
  guardianSection: "Parent or Guardian",
  guardianWhy: (shop: string) =>
    `You are under 18, so a parent or legal guardian consents with you. They fill this part in and sign it themselves, in person, with ${shop}.`,
  guardianStateWants: (state: string) => `What ${state} requires`,
  // 2026-11-03 — where the state sets no rule for this service (minors.ts stateSetsNoRule), the words under it are the shop's own
  guardianShopWants: "What this shop requires",
  guardianName: "Parent or Guardian's Full Name",
  guardianNamePlaceholder: "Their full name",
  guardianRelationship: "Relationship to You",
  guardianRelationshipPlaceholder: "Mother, father, legal guardian",
  guardianSignature: "Parent or Guardian Signature",
  guardianIdPhoto: "Photo of the Parent or Guardian's ID",
  // 2026-11-03 (the live-copy audit): what the shop does with it, where it said "every state that allows this wants a copy of it
  // on file", bigger than the rules (a shop in a state with no rule turns minors on with no state asking)
  guardianIdNote: "Their government photo ID, not yours. The shop keeps a copy on file.",
  needGuardianName: "Enter your parent or guardian's full name.",
  needGuardianRelationship: "Tell us how they are related to you.",
  needGuardianSignature: "Your parent or guardian must sign the form.",
  // TW-0267 — A STATEMENT THE STATE'S RULE HAS THE PARENT OR GUARDIAN SIGN WORD FOR WORD: Tennessee's certification for a
  // minor's piercing (minors.ts guardianAttestationFor), a box they tick above their signature. The statement itself is
  // the state record's and stays English in both languages, because the rule sets the words; these are the line above it
  // and the refusal.
  guardianCertificationLead: (state: string) =>
    `${state} requires the parent or guardian to sign this statement, in these exact words:`,
  // TW-0526 (M01) — the lead over a statement in our words carrying the rule's (the parent's word that they are present)
  guardianPresenceLead: (state: string) => `${state}'s rule has the parent or guardian confirm this before they sign:`,
  // TW-0526, the client-form side (2026-10-29) — the lead over a rule's own words that carry a Spanish rendering beside them
  // (Illinois's provision for a piercing in the mouth, which the rule sets "in substantially the following form")
  guardianTranslatedLead: (state: string) => `${state}'s rule has the parent or guardian sign this statement, in the rule's own words:`,
  needGuardianCertification: (state: string) => `Your parent or guardian must tick ${state}'s statement before they sign.`,
  // the four refusals that name the state's own rule. The first two take the SERVICE (TW-0150):
  // a client here for a tattoo must be told about the tattoo rule, and in Oregon the two are
  // opposite answers, so the wrong noun here is not a wording slip but a wrong statement of law.
  // TW-0279 — AND NO ELSE STATES A LAW. Each service its own branch: a tattoo and a piercing name the state's rule, as they
  // always have; a COSMETIC TATTOO names no state's rule at all, because none is applied to it (it takes no minor in any
  // state, the product's first cut, docs/COSMETIC.md "Adults only") and the honest sentence is the API's own,
  // app.COSMETIC_MINOR_REFUSAL; anything else gets the plain 18+, never the piercing law it used to fall through to.
  minorStateNo: (state: string, service: string) =>
    service === "tattoo"
      ? `${state} does not allow a tattoo under 18, even with a parent's consent.`
      : service === "piercing"
        ? `${state} does not allow a piercing under 18, even with a parent's consent.`
        : service === "cosmetic"
          ? "You must be 18 years old to get a cosmetic tattoo."
          : "You must be 18 years old.",
  minorEarlobeNote: "Ear lobes are often treated differently — ask your piercer.",
  // TW-0225 — WHERE THE STATE WRITES THE EAR OUT OF ITS MINORS RULE (minors.ts earExemptionOf). One question, under the
  // spot the client picked, once that spot is one the carve-out reaches, worded from how far it reaches (`scope`: the
  // whole ear; the lobe and the outer rim, none of the cartilage inside the rim, Mississippi's "outer perimeter or lobe of
  // the ear" since 2026-10-31; or the lobe with no cartilage) and whether it holds only for a single-use stud-and-clasp
  // gun (`method`), which a needle through the lobe is not. A yes takes the state's own requirements, its floor and its
  // flat no off this signing; the parent still consents and signs.
  earAsk: (scope: string, method: string): string =>
    scope === "ear"
      ? method === "stud_gun"
        ? "Is this an ear piercing done with a single-use stud-and-clasp gun?"
        : "Is this an ear piercing?"
      : scope === "perimeter"
        ? method === "stud_gun"
          ? "Is this a piercing of the earlobe or the ear's outer rim (helix), and not the tragus, conch or other cartilage inside the rim, done with a single-use stud-and-clasp gun?"
          : "Is this a piercing of the earlobe or the ear's outer rim (helix), and not the tragus, conch or other cartilage inside the rim?"
        : method === "stud_gun"
          ? "Is this an earlobe piercing, with no cartilage, done with a single-use stud-and-clasp gun?"
          : "Is this an earlobe piercing, with no cartilage?",
  earNeedleIsNo: (scope: string): string =>
    scope === "ear"
      ? "A needle through the ear is a no."
      : scope === "perimeter"
        ? "A needle through the lobe or the rim is a no."
        : "A needle through the lobe is a no.",
  earOutside: (state: string) =>
    `${state}'s rule for a minor's piercing does not reach this one. Your parent or guardian still consents and signs.`,
  // …and under a refusal the carve-out would lift (Mississippi's flat no, Idaho's floor), which spot to pick
  minorEarOutsideNote: (scope: string, method: string): string =>
    scope === "ear"
      ? method === "stud_gun"
        ? "An ear pierced with a single-use stud-and-clasp gun is outside this rule: pick Earlobe or Ear cartilage as the spot below."
        : "An ear piercing is outside this rule: pick Earlobe or Ear cartilage as the spot below."
      : scope === "perimeter"
        ? method === "stud_gun"
          ? "An earlobe or the ear's outer rim pierced with a single-use stud-and-clasp gun is outside this rule: pick Earlobe or Ear cartilage as the spot below."
          : "A piercing of the earlobe or the ear's outer rim is outside this rule: pick Earlobe or Ear cartilage as the spot below."
        : method === "stud_gun"
          ? "An earlobe pierced with a single-use stud-and-clasp gun is outside this rule: pick Earlobe as the spot below."
          : "An earlobe piercing is outside this rule: pick Earlobe as the spot below.",
  // TW-0595 — under a floor whose own words except the earlobe (Arkansas's, Kentucky's): the earlobe is under no floor there,
  // whatever pierces it, and everything else the rule asks still applies, so the sentence names the spot and keeps the parent
  minorFloorEarlobeNote:
    "An earlobe piercing is the exception to that age, with a parent or guardian and everything else the rule asks: pick Earlobe as the spot below.",
  // TW-0153 — TX and TN allow a minor's tattoo ONLY to cover an existing one, so the form asks
  // rather than producing a consent for something the state does not permit
  minorCoverupAsk: "Is this tattoo covering an existing tattoo?",
  minorCoverupYes: "Yes",
  minorCoverupNo: "No",
  minorCoverupOnly: (state: string) =>
    `${state} only allows a tattoo under 18 to cover an existing tattoo.`,
  // TW-0279 — three-way for minorStateNo's reason above
  minorUnderFloor: (state: string, floor: string, service: string) =>
    service === "tattoo"
      ? `${state} does not allow a tattoo under ${floor}, even with a parent's consent.`
      : service === "piercing"
        ? `${state} does not allow a piercing under ${floor}, even with a parent's consent.`
        : service === "cosmetic"
          ? "You must be 18 years old to get a cosmetic tattoo."
          : "You must be 18 years old.",
  minorPlacementNo: (state: string, placement: string) =>
    `${state} does not allow a ${placement.toLowerCase()} piercing under 18, whatever a parent signs. Pick another spot, or come back at 18.`,
  minorPlacementUnverifiable: (state: string) =>
    `${state} bars some piercings under 18, so pick the exact spot rather than "Somewhere else".`,
  minorCite: (cite: string) => `The rule: ${cite}`,
  // TW-0153 — a county or an independent city that is stricter than its state. The refusal above
  // already names the jurisdiction, because minorStateNo and minorUnderFloor take whatever
  // jurisdiction set the rule; this is the line that stops a client reading it as the state's, and
  // it stands in for minorCite, whose cite would be the STATE's rule and therefore the wrong one.
  minorCountyRule: (state: string) => `This is a local rule, stricter than ${state}'s.`,
  // TW-0153 — for a state whose rule treats an emancipated minor as an adult. Asked ABOVE the guardian
  // section, because a yes closes it: there is no parent to sign, which is what emancipation means.
  minorEmancipatedAsk: "Are you an emancipated minor with a certified copy of your court decree?",
  // TW-0153 — where a state or county wants BOTH IDs, the minor's own goes on file beside the
  // parent's. Two uploaders, each saying whose it is, because one labelled uploader was the whole
  // bug: the shop got the parent's twice and the minor's never.
  minorIdPhoto: "Photo of the minor's ID",
  minorIdNote: "Your state asks for the minor's own photo ID as well as the parent's. A school ID or a passport is fine if they have no license.",
  // 2026-09-24 — the birth certificate or guardianship papers, where the shop or the state asks for them
  // (minors.ts asksGuardianProof). The paper that shows this adult IS the parent or guardian.
  guardianProofPhoto: "Photo of the birth certificate or guardianship papers",
  guardianProofNote: "The minor's birth certificate, or the adoption, custody or guardianship order: the paper that shows this adult is the parent or legal guardian.",
  needGuardianProofPhoto: "Add a photo of the birth certificate or guardianship papers.",
  // TW-0270 slice 5 (2026-10-16) — A PAPER THE STATE'S RULE KEEPS WITH A MINOR'S RECORD (minors.ts papersAsked): a notarized
  // consent, the department's own form, an affidavit, the certified copy of an emancipation decree. `name` is the rule's own
  // words for it and `name_es` the Spanish (awaiting a native read); `place` is the state, or the county whose rule it is.
  paperPhoto: (paper: { name: string; name_es: string }) => `Photo of the ${paper.name}`,
  paperNote: (place: string) => `${place}'s rule asks for this paper. The photo is kept with this waiver.`,
  needPaperPhoto: (paper: { name: string; name_es: string }) => `Add a photo of the ${paper.name}.`,
  // TW-0279 slice 10 — THE CLIENT'S HEALTH CARE PROVIDER'S WRITTEN INSTRUCTIONS, on a cosmetic signing of areola or
  // camouflage work, or scalp work that covers scars (cosmetic.ts providerInstructionsNeeded), where the shop's state's rule
  // names them (New Jersey's, whose own plain sentence stands under the photo) or the shop's own switch is on, when this
  // sentence does. The scalp question comes first, because scalp work for thinning hair alone needs no doctor's paper. The
  // photo is OPTIONAL since the same night (slices 8 and 10 made one shape): the client signs without it, and the shop adds it
  // to the record from its dashboard before the artist signs, so there is no sentence for its refusal.
  providerInstructionsPhoto: "Photo of your doctor's written instructions",
  providerInstructionsNote: "The instructions your doctor or physician assistant wrote for this work.",
  providerInstructionsByShop: (shop: string) => `${shop} keeps your doctor's written instructions for this work with your record.`,
  scalpScarsAsk: "Is this scalp work covering scars, such as from a hair transplant, a surgery or an injury?",
  needScalpScarsAnswer: "Answer yes or no: is this scalp work covering scars?",
  // TW-0227 — THE OTHER PARENT'S WRITTEN PERMISSION, where the shop's city or county wants it from both parents (Hammond
  // and East Chicago: "written permission from both parents or guardians, if not deceased") or wants both of them there
  // (Delaware County: "Both parents or minor’s legal guardian must be present and provide consent in writing."). Inside
  // the parent's box, after their own signature; the parent who is there picks ONE of three ways, and where both have to
  // be there the photo is not one of them (minors.ts requiresBothParentsPresent). `who` is the place whose rule it is.
  // The sole-parent sentence is a consent the signed waiver quotes, so its Spanish is consent-sentences-es.json's and its
  // English is app.py SOLE_PARENT_EN word for word, each held by a test.
  secondParentTitle: "The other parent's written permission",
  secondParentWhy: (who: string) => `${who} asks for written permission from both parents, or from the legal guardian.`,
  secondParentWhyPresent: (who: string) => `${who} asks for both parents, or the legal guardian, to be here and sign.`,
  secondParentSigns: "The other parent signs here",
  secondParentPhotoOption: "A photo of the other parent's signed permission",
  soleParent: "There is no second parent — the other parent is deceased, or one guardian has sole custody",
  secondParentName: "Other Parent's Full Name",
  secondParentNamePlaceholder: "Their full name",
  secondParentSignature: "Other Parent's Signature",
  secondParentPhoto: "Photo of the other parent's signed permission",
  secondParentPhotoNote: "The note the other parent signed, giving their permission. Get the whole page in, where it can be read.",
  needSecondParentChoice: "Choose how the other parent gives their written permission.",
  needSecondParentName: "Enter the other parent's full name.",
  needSecondParentSignature: "The other parent must sign the form.",
  needSecondParentPhoto: "Add a photo of the other parent's signed permission.",
  minorEmancipatedNote: (state: string) =>
    `${state} treats an emancipated minor as an adult, so you sign this yourself. Bring the certified decree — the shop has to see it.`,

  // the bottom of the page
  signedDate: "Signed Date",
  // TW-0488 (2026-10-26) — THE CLIENT'S ID, BY WHAT IT IS AND NEVER BY ONE KIND OF CARD (idType.ts has the morning it was
  // earned: a Florida shop's visitors from abroad, told by "Driver's License" over the camera that the form wanted a card they
  // did not have). Until this day the label said "Driver's License" and the refusal "...of your driver's license."
  idPhoto: "Photo of Your ID",
  idPhotoNote: "A driver's license, state ID, passport or other government photo ID.",
  idTypeQuestion: "Which ID is it?",
  idTypeNames: {
    drivers_license: "Driver's license",
    state_id: "State ID",
    passport: "Passport",
    other: "Other government ID",
  } as Record<IdType, string>,
  // where the shop's state's rule keeps the number (Maine, Missouri): the box, its hint for the ID picked, and why it is asked
  idNumber: "ID Number",
  idNumberPlaceholder: {
    drivers_license: "The number on your license",
    state_id: "The number on your ID card",
    passport: "The number on your passport's photo page",
    other: "The number on your ID",
    "": "The number on your ID",
  } as Record<IdType | "", string>,
  idNumberWhy: (state: string) => `${state}'s rule keeps this ID's number on the record. Type it as it reads on the ID.`,
  takePhoto: "Take photo",
  // TW-0171 — the second way in, for an ID that lives on the phone (Chris, 2026-09-11: "Choose from
  // library"), where the shop allows it. The two below are what a client reads when a pick fails.
  chooseFromLibrary: "Choose from library",
  openingPhoto: "Opening photo...",
  savedPhotoUnreadable: "That photo could not be opened. Try another one, or take a photo.",
  savedPhotoTooBig: "That photo is too big to use. Try another one, or take a photo.",
  retakePhoto: "Retake photo",
  clearPhoto: "Clear photo",
  retake: "Retake",
  usePhoto: "Use Photo",
  // 2026-10-26 (the form audit) — THE CAMERA SCREEN'S OTHER WORDS: the shutter and the X had no name a screen reader could say,
  // and a camera the browser refused left a black screen with no words at all (PhotoUploader.tsx says what it does now)
  cameraShutter: "Take the photo",
  cameraClose: "Close the camera",
  cameraBlocked: "The camera didn't open here. You can take the photo with your phone's own camera instead.",
  cameraApp: "Open my phone's camera",
  cameraPreview: "The photo you just took",
  clientSignature: "Client Signature",
  signHere: "Sign here",
  clear: "Clear",
  needleBatch: "Needle Batch #",
  // 2026-10-31 — under a lot photo the shop requires (lotPhotos.ts), so the client sees it before the refusal says it
  lotPhotoRequired: "Required",
  inkBatch: "Ink Batch #",
  // TW-0279 — a cosmetic tattoo's second lot photo is of its pigment
  pigmentBatch: "Pigment Batch #",
  submit: "Submit",
  submitting: "Submitting...",

  // the sentences a client reads when a tap did nothing
  invalidClient: "Invalid client",
  // TW-0279 — the choices the shop's form offers, named (chooseServiceForAcks above has why)
  tellUsService: (works: string | string[]) => `Tell us whether today is ${orList(works, "or")}.`,
  // TW-0541 (2026-10-29) — the browser's own stop at a required box, said in the page's words: an iPhone focuses the box and
  // shows nothing, so the page says it under the Submit button, and under the box itself (refusal.ts BY_DETAIL has the measure)
  aboveStillEmpty: "A box above is still empty. Scroll up to the one marked in red.",
  aboveNeedsAFix: "A box above needs a fix. Scroll up to the one marked in red.",
  pickOneHere: "Pick one from the list.",
  fillThisIn: "Fill this in to sign.",
  fixThisBox: "Fix this to sign.",
  noInfluence: "You cannot be under the influence of drugs or alcohol.",
  noPregnant: "You cannot be pregnant or nursing.",
  needCommunicableDetails: "You must provide details about your communicable disease.",
  // 2026-10-01 — a health question the page asked and nobody answered (healthQuestions.ts), named in the page's own words
  needHealthAnswer: (question: string) => `Answer yes or no: ${question}`,
  // 2026-10-01 — a yes whose details box was left empty (healthQuestions.ts DETAILS_BOX), the question named the same way
  needHealthDetails: (question: string) => `Add the details of your yes: ${question}`,
  needTerms: "You must accept the terms and conditions to sign.",
  // 2026-10-26 (the form audit) — under every box the signature depends on that is still unticked, while a box refusal is up:
  // the submit names the first, and this marks the rest, so one pass ticks them all
  tickThisBox: "Tick this box to sign.",
  // 2026-10-02 — the card-fee box (cashPreferred.ts), when the shop has it on and it was left unticked
  needCashPreferred: "You must tick the box about paying by card to sign.",
  // 2026-10-08 — A SHOP IN HIPAA MODE (hipaaForm.ts; the API's hipaa_form.py holds the same English, a test there reads it
  // here): the practice's Notice of Privacy Practices, its box in the brief's words and the sentence when it is left
  // unticked; and the photo release, a HIPAA authorization the client signs apart from the waiver, optional, each line one
  // element of 45 CFR 164.508(c). {shop} is the shop's name and {years} its period ("2 years"), filled by hipaaForm.ts.
  privacyNoticeLead: "Read this practice's notice, then tick the box under it.",
  privacyNoticeAck: "I received this practice's Notice of Privacy Practices",
  needPrivacyNotice: "Tick the box that says you received this practice's Notice of Privacy Practices, then press Submit again.",
  photoReleaseHeading: "Photo release (optional)",
  photoReleaseLines: [
    "This is a HIPAA authorization, and you can say no: {shop} does your work the same either way.",
    "What it covers: photos {shop} takes of your skin and of the work, before, during and after your appointment.",
    "Who may use them: {shop} and the people who work there.",
    "Who may see them: anyone who views {shop}'s portfolio, its website or its social media.",
    "What for: to show {shop}'s work.",
    "How long: {years} from the day you sign, unless you take it back first.",
    "Taking it back: tell {shop} in writing at any time. That stops any new use from then on; it cannot undo what was already shared.",
    "Once a photo is shared, anyone who sees it can share it again, and the privacy law that protects your health records no longer protects it.",
    "{shop} gives you a copy of this release.",
  ] as string[],
  photoReleaseBox: "Yes, I sign this photo release.",
  yearsWords: (n: number) => (n === 1 ? "1 year" : `${n} years`),
  // 2026-09-23 — the payment question, when the shop asks and nothing was picked (the tellUsService shape: a tap is missing)
  needPaymentMethod: "Tell us how you are paying today.",
  // TW-0488 — the three the ID step can say: the type, where the state's rule keeps it; the number, where it keeps that; the photo
  needIdType: "Tell us which ID this is.",
  needIdNumber: "Type the number on your ID.",
  needIdPhoto: "You must upload a photo of your ID.",
  needSignature: "You must sign the form.",
  needNeedlePhoto: "You must upload a photo of the needle batch number.",
  needInkPhoto: "You must upload a photo of the ink batch number.",
  submitError: (code: number) => `There was an error submitting the form (${code}).`,
  // 2026-10-26 (the form audit) — THE POST NEVER REACHED US: a counter's wifi dropped, a phone left the signal. Until today the
  // sentence was "Error submitting form: " and the browser's own words after it, in English on either page ("Load failed" on an
  // iPhone, "Failed to fetch" on Chrome), which told nobody whether the waiver went or what to do. It did not go, nothing on
  // the page is lost, and pressing Submit again is the whole of what to do.
  submitOffline: "Your waiver didn't send. Check your connection, then press Submit again. Everything you filled in is still here.",
  // TW-0505 (2026-10-29) — THE SHOP'S STATE'S RULES ARRIVE AFTER THE PAGE (stateRules.ts): a Submit pressed while they are on
  // their way posts nothing, and a page whose file and fallback both failed says so at its top and under the button. Plain
  // words for a client who never needs to know what a state file is: the rest of the form, still loading, or not loaded.
  stateRulesLoading: "One moment: the rest of this form is still loading. Press Submit again in a few seconds.",
  stateRulesFailed: "Part of this form didn't load. Check your connection, then tap Try again. Everything you filled in is still here.",
  stateRulesTryAgain: "Try again",
  invalidBirthDate: "Please enter a valid birth date.",

  // the done screen. 2026-11-03 (the live-copy audit, docs/audits/live-copy-voice-audit-2026-11-02.md): "Signed." over the
  // shop holding the signed waiver, where it said "Success!" over "Your waiver has been submitted." from 2023: the one "!" a
  // client read at every signing, and a passive sentence. A shop not loaded yet is "The shop" (the page has its name by then).
  success: "Signed.",
  submitted: (shop: string) => (shop.trim() ? `${shop.trim()} has your signed waiver.` : "The shop has your signed waiver."),
  ownerDone: "Shop owner trying your own form? This one is in your inbox and your dashboard now.",
  backToQr: "Back to your QR code",
  download: "Download your waiver",
  submitAnother: "Submit another waiver",
  // 2026-10-08 — THE CLIENT'S OWN COPY, on the done screen of a shop in HIPAA mode whose state's rule hands the client one
  // (clientCopy.ts says what it draws and why). Its two section headings are the page's own (`acknowledgment`,
  // `questionnaire`), its labels the page's (`name`, `signedDate`), and its answers the radios' (`yes`, `no`).
  copyHeading: (shop: string) => `Your copy of what you signed at ${shop}`,
  copyShopPrints: (shop: string) => `${shop} will print you a copy too.`,
  copyPrint: "Print or save",
  copyReleaseDeclined: "You did not sign the photo release.",
  // the one health question a client may leave blank (healthQuestions.ts OPTIONAL_HEALTH_QUESTIONS), and a written box left empty
  copyNotAnswered: "Not answered",
  copyNone: "None",

  // the locked screen (the shop's free waivers used up)
  lockedTitle: (shop: string) => `${shop} needs to activate its waiver page`,
  lockedBody: (price: string) =>
    `The free waivers on this page are used up. Whoever runs the shop: tap below to activate it. ${price} a year, unlimited waivers. This page works again the moment it goes through.`,
  activate: (shop: string) => `Activate ${shop}`,
  notTheOwner: "Not the owner? Hand this phone to someone who works here.",

  // TW-0517 (2026-10-27) — A COLD PAGE NOBODY HAS CLAIMED: minted for the shop the day its cold placard was ordered, live from its
  // first scan, and nobody's until somebody adds an email (the claim) or pays (Stripe's email claims it). Plain, his register; the
  // price a numeral. The banner at the top names the page and that it works; its door is the owner's, and a phone that dismisses it
  // sees it again at the tenth signing, where the trial banner's line carries the two doors.
  coldBanner: (shop: string) => `This is ${shop}'s waiver page. It's live.`,
  coldAddEmail: "Add your email",
  coldAddLogo: "Add your logo",
  coldDismiss: "Dismiss",
  coldPay: (price: string) => `Pay ${price} a year`,
  coldSetUp: "Set up your account",

  // TW-0286 — a second person at a paused shop gets the form 10 more waivers, once (topUp.ts): one quieter line and one
  // email box under Activate, drawn only when the promotions service offers it. The person is told plainly that the
  // owner hears it was them, with them copied, because that is what the note does. The refusals are the door's codes,
  // each its own sentence; `topUpTryAgain` is every answer the screen cannot name.
  topUpLine: (n: number) => `Work here but don't run it? Get ${plural(n, 'more waiver')} while the shop decides.`,
  topUpEmailLabel: "Your email",
  topUpButton: (n: number) => `Get ${plural(n, 'more waiver')}`,
  topUpBusy: (n: number) => `Adding ${n} more…`,
  topUpNote: "We'll email the owner that you asked, and copy you.",
  topUpOwnerEmail: "That's the shop's own email, so tap Activate above.",
  topUpAlready: (n: number) => `This page already got its ${plural(n, 'more waiver')}. Whoever runs the shop can activate it above.`,
  topUpBadEmail: "That email does not look right.",
  topUpNotOpen: "This page can't get more waivers. Whoever runs the shop can activate it above.",
  topUpTryAgain: "We couldn't add them just now. Try again in a minute.",

  // TW-0158 — the banner above a RUNNING form, once the free ten are spent. Read by a stranger who
  // scanned a QR code to get tattooed, so it names its audience in the first two words and never
  // addresses the reader. "your" would make a client think the note is about them.
  overTrialNote: (lock: number) =>
    `For the shop: the free waivers are used up. This page pauses at ${lock}.`,
  overTrialAction: (price: string) => `Activate for ${price} a year`,

  // the screen when the address has no shop behind it, or we could not reach ours (TW-0097). The
  // wildcard CDN answers every *.waivers.tattoo, so a retired, renamed or mistyped subdomain gets
  // the real form bundle and then has nothing to render. Two situations, and they want different
  // sentences: nobody is here, versus we are broken. Neither says "client" — in this product a
  // client is the person signing, so "no client found" told them THEY were not found.
  noShopTitle: "There's no shop at this address.",
  noShopBody: "Check the link, or scan the shop's QR code.",
  // TW-0593 — the screen when the shop closed its account with us: the shop exists, so the person is told to ask it
  closedTitle: "This shop's waiver form is closed.",
  closedBody: "Ask the shop how they'd like you to sign.",
  loadFailedTitle: "We couldn't load this page.",
  loadFailedBody: "Try again in a moment. If it keeps happening, tell the shop.",

  // the screen when the page itself throws (TW-0132). Distinct from loadFailed, which is about the
  // SHOP not loading: this one is our code breaking after the shop is already on screen. It never
  // says the shop has the waiver — a crash can happen before a client has signed anything, and a
  // reassurance that turns out to be false is worse than no words at all.
  crashTitle: "Something went wrong on this page.",
  crashBody: "Reload and try again. If it keeps happening, tell the shop.",
};

export type Strings = typeof EN;

// TW-0224 — a Spanish noun carries its gender, so the demo's "for a tattoo / for a piercing" is "un tatuaje" and
// "una perforación". The sentences said "un ${work}" for both until a piercing could reach them, which read "un
// perforación". Keyed on the two nouns this file itself hands the demo (demoWorkTattoo, demoWorkPiercing).
const unEs = (work: string): string => `${work === "perforación" ? "una" : "un"} ${work}`;

const ES: Strings = {
  languageName: EN.languageName,
  switchLabel: "Idioma",

  clientInformation: "Datos del cliente",
  welcomeBack: "Hola de nuevo",
  welcomeBackCheck: " — revisa que todo siga correcto.",
  // TW-0500. Awaiting a native-speaker read like the rest of the ES copy in this file.
  draftKept: "Guardamos lo que escribiste.",
  draftStartOver: "Empezar de nuevo",
  // TW-0195. Awaiting a native-speaker read like the rest of the ES copy in this file.
  beenHereBefore: "¿Ya viniste antes?",
  lookupLabel: "Encuentra tus datos de la última vez",
  lookupIntro: "Escribe el teléfono y el correo que usaste la última vez y llenamos el resto.",
  lookupFind: "Búscame",
  lookupFinding: "Buscando…",
  lookupClose: "Cerrar",
  lookupMissed: "No te encontramos. Llena el formulario de abajo.",
  // TW-0195, the demo. Awaiting a native-speaker read like the rest of the ES copy in this file.
  lookupDemoHint:
    "Esto es una demostración. Toca \"Búscame\" y el formulario se llena con un cliente inventado, igual que lo haría con uno de los tuyos.",
  questionnaire: "Cuestionario previo",
  acknowledgment: "Reconocimiento y exención de responsabilidad",
  batchNumbers: "Lotes de agujas y tinta",
  // TW-0279 — pendiente de la lectura de un hablante nativo, como todo el español de cosmético en este archivo
  batchNumbersCosmetic: "Lotes de agujas y pigmento",
  whatToday: "¿Qué te vas a hacer hoy?",

  name: "Nombre",
  firstName: "Nombre",
  lastName: "Apellido",
  birthDate: "Fecha de nacimiento",
  youAre: (age: number) => `Tienes ${age} ${age === 1 ? "año" : "años"}.`,
  mm: "MM",
  dd: "DD",
  yyyy: "AAAA",
  month: "Mes",
  day: "Día",
  year: "Año",
  phone: "Teléfono",
  phonePlaceholder: "(000) 000-0000",
  email: "Correo electrónico",
  emailPlaceholder: "tu@ejemplo.com",
  didYouMeanBefore: "¿Quisiste decir ",
  didYouMeanAfter: "?",
  address: "Dirección",
  streetAddress: "Calle y número",
  addressLine2: "Apto., suite, unidad (opcional)",
  city: "Ciudad",
  state: "Estado",
  zip: "Código postal",
  addressSuggestions: "Sugerencias de dirección",
  poweredByGoogle: "Powered by Google",

  emergencySection: "Contacto de emergencia",
  emergencyWhy: (shop: string) => `Alguien a quien ${shop} pueda llamar si necesitas ayuda.`,
  emergencyName: "Nombre",
  emergencyNamePlaceholder: "Su nombre completo",
  emergencyRelationship: "Parentesco",
  emergencyRelationshipPlaceholder: "ej.: mamá, pareja, amigo",
  emergencyPhone: "Teléfono",
  emergencyAddress: "Dirección",
  emergencyAddressPlaceholder: "Calle, ciudad, estado",
  needEmergencyName: "Escribe el nombre de tu contacto de emergencia.",
  needEmergencyRelationship: "Dinos qué parentesco tiene contigo tu contacto de emergencia.",
  needEmergencyPhone: "Escribe el teléfono de tu contacto de emergencia.",
  needEmergencyAddress: "Escribe la dirección de tu contacto de emergencia.",
  physicianSection: "Médico",
  physicianWhy: (shop: string) => `Tu médico, si tienes uno, para que ${shop} pueda contactarlo si hace falta. Puedes dejarlo en blanco.`,
  physicianName: "Nombre",
  physicianNamePlaceholder: "Su nombre completo",
  physicianPhone: "Teléfono",
  physicianAddress: "Dirección",
  physicianAddressPlaceholder: "Calle, ciudad, estado",

  todayIm: "Hoy me hago",
  aTattoo: "Un tatuaje",
  aPiercing: "Una perforación",
  aCosmetic: "Un tatuaje cosmético",
  nounTattoo: "un tatuaje",
  nounPiercing: "una perforación",
  nounCosmetic: "un tatuaje cosmético",
  chooseOne: "Elige una opción",
  cosmeticProcedures: "Procedimientos",
  cosmeticProceduresHint: "Marca todos los que te vas a hacer hoy.",
  cosmeticVisit: "Esta visita es",
  needCosmeticProcedure: "Marca lo que te vas a hacer hoy.",
  needCosmeticVisit: "Dinos qué es esta visita.",
  jewelryMaterial: "Material de la joya",
  jewelryNotes: "Notas sobre la joya",
  optional: "opcional",
  jewelryNotesPlaceholder: "16g, 8mm flatback",
  jewelryNotesHint: "Calibre, tamaño, marca: lo que tu perforador quiera dejar registrado",
  // TW-0154 — el precio del trabajo, solo cuando la tienda lo activa
  priceLabel: (service: string) =>
    `Precio ${service === "piercing" ? "de la perforación" : service === "cosmetic" ? "del tatuaje cosmético" : "del tatuaje"}`,
  pricePlaceholder: "ej.: $150",
  priceKindFixed: "Tarifa acordada",
  priceKindRange: "Rango estimado",
  priceKindHourly: "Tarifa por hora",
  priceRangeJoin: "a",
  pricePerHour: "/hora",
  priceAmount: "Monto",
  priceFrom: "Desde",
  priceTo: "Hasta",
  priceRate: "Monto por hora",
  priceOnWaiver: (price: string) => `En tu formulario: ${price}`,
  // 2026-09-23. Awaiting a native-speaker read like the rest of the ES copy in this file.
  paymentMethodQuestion: "¿Cómo pagas hoy?",
  paymentCash: "Efectivo",
  paymentCard: "Tarjeta",
  paymentOther: "Otro",

  yes: "Sí",
  no: "No",
  qInfluence: "¿Estás bajo los efectos de drogas o alcohol?",
  qPregnant: "¿Estás embarazada o amamantando?",
  qCommunicable: "¿Tienes alguna enfermedad contagiosa?",
  qSkin: "¿Tienes alguna afección de la piel?",
  qSkinCosmetic:
    "¿Tienes alguna afección de la piel, como eccema, psoriasis o rosácea, o la piel donde te vas a hacer esto está quemada por el sol, irritada, con granitos o todavía sanando?",
  communicableDetails: "Enfermedad contagiosa (por ejemplo, VIH, hepatitis, etc.)",
  skinDetails: "Afecciones de la piel (por ejemplo, sarpullido, eccema, infección, psoriasis, pecas, etc.)",
  ifYesIdentify: "Si respondiste que sí, indica cuál.",
  tellYourPiercer: "Cuéntale los detalles a tu perforador.",
  tellYourArtist: "Cuéntale los detalles a tu artista.",
  medicalHistory:
    "¿Puedes contarnos sobre tu historial médico? Por ejemplo: diabetes, enfermedades cardiovasculares, epilepsia o convulsiones, desmayos, narcolepsia, enfermedades de la sangre, trastornos inmunitarios, cicatrices queloides, etc.",
  medications: (service: string) =>
    service === "cosmetic"
      ? "¿Tomas algún anticoagulante u otro medicamento que pueda afectar el tatuaje cosmético o la cicatrización, o has tomado aspirina, ibuprofeno o naproxeno en las últimas 24 horas? Danos los detalles."
      : `¿Tomas actualmente algún medicamento, incluidos anticoagulantes, que pueda afectar ${service === "piercing" ? "la perforación" : "el proceso de tatuaje"} o la cicatrización? Danos los detalles.`,

  // las preguntas propias del estado (TW-0104)
  qAllergies: "¿Eres alérgico o sensible a algún medicamento, pomada, jabón, látex u otra solución tópica?",
  qAllergiesCosmetic:
    "¿Eres alérgico o sensible a algún medicamento, pomada, jabón, látex u otra solución tópica, incluidos la tinta o el pigmento de tatuaje, metales como el níquel, o medicamentos para adormecer como la lidocaína?",
  allergiesDetailsLabel: "Alergias o sensibilidades",
  qBleeding: "¿Tienes o has tenido algún trastorno de la coagulación? (Hemofilia, moretones con facilidad, sangrado que tarda en parar.)",
  bleedingDetailsLabel: "Trastorno de la coagulación",
  qEaten: "¿Has comido en las últimas 4 horas?",
  // TW-0279 slice 8 — la regla de Míchigan sobre los productos para adormecer; pendiente de la lectura de un hablante nativo
  qNumbing: "¿Tienes en la piel una crema, un spray u otro producto para adormecer, o se usará uno hoy?",
  numbingPrescriptionPhoto: "Foto de la receta del médico para el producto para adormecer",
  numbingPrescriptionNote: (state: string) =>
    `${state} no permite hacer el trabajo sobre piel con un producto para adormecer a menos que un médico lo haya recetado, y la receta se guarda con tu registro. ¿No tienes receta? Díselo a tu artista antes de firmar.`,
  // TW-0270 slice 4 — las cinco preguntas que la regla de un estado puede nombrar; pendientes de la lectura de un hablante nativo
  qDiabetesHistory: "¿Alguna vez has tenido diabetes?",
  qSeizuresOrFainting: "¿Alguna vez has tenido epilepsia, convulsiones, desmayos o narcolepsia?",
  qHeartOrBloodPressure: "¿Alguna vez has tenido una afección del corazón, una enfermedad cardíaca o presión arterial alta?",
  qBloodThinnersRecently:
    "En las últimas 24 horas, ¿has tomado un anticoagulante, o aspirina, ibuprofeno, naproxeno u otro antiinflamatorio?",
  qPregnantRecently: "¿Has estado embarazada en los últimos 3 meses?",
  demoPickState: "Ver el formulario para",
  demoStandard: "Cualquier estado",
  demoStateNote: "Algunos estados piden más datos en el registro. Elige uno para ver su formulario.",
  demoPickType: "El estudio hace",
  demoTypeTattoo: "Solo tatuajes",
  demoTypeBoth: "Tatuajes y perforaciones",
  demoTypePiercing: "Solo perforaciones",
  demoTypeCosmetic: "Solo tatuajes cosméticos",
  demoPierceMinors: "Perforamos a menores con su madre, padre o tutor presente",
  demoStateStandard: (state: string) =>
    `${state} usa el formulario estándar: las mismas preguntas que en los demás estados.`,
  demoTypeBothMeans: "Tu cliente elige tatuaje o perforación al firmar, y el formulario lo sigue.",
  demoTypePiercingMeans: "El formulario pregunta por la perforación, la joyería y las alergias a metales.",
  demoTypeCosmeticMeans:
    "El formulario pregunta qué procedimientos y qué es esta visita, hace las preguntas de salud que traen esos procedimientos y muestra los reconocimientos propios de un tatuaje cosmético.",
  demoMinorsAllowed: (state: string, work: string) =>
    `Un cliente menor de 18 años que viene por ${unEs(work)} ve una sección para su madre, padre o tutor, y el formulario firmado imprime lo que exige ${state}.`,
  demoMinorsShopRule: (state: string, work: string) =>
    `Un cliente menor de 18 años que viene por ${unEs(work)} ve una sección para su madre, padre o tutor, y el formulario firmado imprime la regla de tu estudio: ${state} no fija ninguna para ${unEs(work)}.`,
  demoMinorsRefused: (state: string, work: string) =>
    `${state} no permite ${work} a menores de 18 años, así que para eso el formulario sigue siendo solo para mayores de 18 y explica por qué.`,
  demoMinorsCosmetic:
    "Un cliente menor de 18 años no puede firmar para un tatuaje cosmético: el formulario es solo para mayores de 18 en todos los estados.",
  demoWorkTattoo: "tatuaje",
  demoWorkPiercing: "perforación",
  demoWorkCosmetic: "tatuaje cosmético",
  demoMinorsPickState: "Elige un estado arriba: la respuesta depende del estado.",
  demoAskProof: "Pedir el acta de nacimiento o los papeles de tutela",
  demoMinorsShort: "Menores de 18 con su madre, padre o tutor",
  demoProofShort: "Acta de nacimiento o papeles de tutela",
  demoDone: "Listo",
  demoProofOn: "Cuando firma la madre, el padre o el tutor, el formulario también pide una foto del acta de nacimiento o de los papeles de tutela.",
  demoProofByRule: (state: string, works: string | string[]) =>
    `${state} pide por sí mismo una foto del acta de nacimiento o de los papeles de tutela para ${listOf(works).map(unEs).join(" o ")}.`,
  demoCountyRule: (place: string, state: string, works: string | string[]) =>
    `${place} tiene su propia regla para ${listOf(works).map(unEs).join(" o ")} a menores de 18 años, y el formulario la sigue además de la de ${state}.`,
  // TW-0227 — pendiente de la lectura de un hablante nativo, como el resto del español
  demoBothParents: (place: string) =>
    `${place} pide el permiso por escrito de los dos padres, así que el padre o la madre que está aquí elige una de tres: el otro padre o la otra madre firma aquí, una foto del permiso que firmó, o no hay otro padre ni otra madre.`,
  demoBothParentsPresent: (place: string) =>
    `${place} pide que los dos padres estén aquí, así que el padre o la madre que está aquí elige una de dos: el otro padre o la otra madre firma aquí, o no hay otro padre ni otra madre.`,
  demoEar: "Mostrar la perforación de oreja de un menor",
  demoEarShort: "Perforación de oreja",
  demoEarAsks: (state: string, question: string) =>
    `${state} deja algunas perforaciones de oreja fuera de su regla para menores, así que el formulario pregunta: "${question}" Con un sí, pide el consentimiento de la madre, el padre o el tutor y nada de lo que nombra la regla.`,
  demoEarCovered: (state: string) =>
    `La regla de ${state} para la perforación de un menor alcanza a la oreja como a cualquier otro lugar, así que una perforación de oreja pide lo mismo que cualquier perforación.`,
  demoNeedsState: "Elige primero un estado",
  demoReset: "Restablecer",
  demoCustomize: "Lo que hace este estudio",
  demoShowQr: "Ver el código QR para abrirlo en tu teléfono",
  demoQrTitle: "Escanea para abrir esta demo en tu teléfono",
  demoQrNote: "Se lleva todo lo que has configurado aquí.",
  demoQrClose: "Cerrar",
  demoOnPhoneTitle: "Lo estás completando en tu teléfono",
  demoOnPhoneBody: "Tus clientes reciben este mismo formulario en su propio teléfono, desde tu código QR.",
  demoOnPhoneBack: "Mejor llenarlo aquí",
  pickerWaiver: "Firma tu formulario",
  pickerDeposit: "Paga un depósito",
  demoBackToWaiver: "Volver al formulario",
  demoStateAdds: (state: string) => `${state} añade`,
  changeAllergies: "una pregunta sobre alergias",
  changeBleeding: "una pregunta sobre trastornos de la coagulación",
  changeMedications: "la pregunta sobre medicamentos",
  changeLots: "fotos de los lotes de agujas y tinta",
  changeLicense: "el número de licencia del artista",
  changeEmergencyContact: "un contacto de emergencia",
  // Las diez que faltaban (2026-09-21); palabras de una sesión de Fable, a la espera de una lectura nativa.
  changeEatenRecently: "una pregunta sobre si comió en las últimas 4 horas",
  changeBloodDonor: "el aviso sobre la donación de sangre",
  changeGrievance: "cómo presentar una queja ante el estado",
  changeHealthAuthority: "cómo comunicarse con la junta de salud del condado",
  changeAftercareSuggestions: "las sugerencias de cuidado posterior por escrito",
  changeStateDisclosure: "la declaración de divulgación propia del estado",
  changeDisclosureRequest: "la petición propia del estado de que nos digas tus condiciones",
  changeRiskTattoo: "los riesgos de un tatuaje que enumera el estado",
  changeRiskPiercing: "los riesgos de una perforación que enumera el estado",
  changeCautionaryNotice: "un aviso de precaución que el cliente marca",
  changeConsentStatements: "las declaraciones de consentimiento que enumera el estado",
  changeComplicationsInformation: "una casilla para la información del estado sobre las complicaciones",
  changeDeclineStatement: "la declaración de que el trabajo se puede rechazar",
  changeNumbingPrescription: "una pregunta sobre productos para adormecer, y una foto de la receta si la respuesta es sí",
  changeDiabetesHistory: "una pregunta sobre la diabetes",
  changeSeizuresOrFainting: "una pregunta sobre epilepsia, convulsiones o desmayos",
  changeHeartOrBloodPressure: "una pregunta sobre afecciones del corazón y presión arterial alta",
  changeBloodThinnersRecently: "una pregunta sobre anticoagulantes y antiinflamatorios en las últimas 24 horas",
  changePregnantRecently: "una pregunta sobre embarazo en los últimos 3 meses",
  // 2026-10-28 (TW-0526); palabras de una sesión de Opus, a la espera de una lectura nativa.
  changeAftercareSuggestionsPiercing: "las sugerencias de cuidado de la perforación por escrito",
  changeProcedureExplanationTattoo: "una explicación por escrito de cómo se hace el tatuaje",
  changeProcedureExplanationPiercing: "una explicación por escrito de cómo se hace la perforación",
  changeInkRegulatoryStatus: "una declaración destacada sobre si las tintas están aprobadas",
  changeAftercareReceived: "una casilla que confirma que se recibieron las instrucciones de cuidado",
  license: "licencia",

  chooseServiceForAcks: (works: string | string[]) => `Elige arriba ${orList(works, "o")}, y aquí aparecerá lo que aceptas.`,
  acceptTerms: "Acepto los ",
  termsLink: "términos y condiciones",
  acceptTermsEnd: ".",
  // The two Spanish sentences that also live in consent-sentences-es.json (`optional`), because the
  // PDF prints them: i18n.test.ts fails when either stops being the same sentence.
  hearFromShop: (shop: string) =>
    `Sí, ${shop} puede enviarme textos o correos después de hoy sobre mis cuidados posteriores y cómo va la cicatrización. Opcional. Pueden aplicarse tarifas por mensajes y datos; responde STOP cuando quieras.`,
  shopMarketing: (shop: string) =>
    `Sí, ${shop} puede enviarme textos o correos con sus novedades, ofertas y eventos. Opcional. Pueden aplicarse tarifas por mensajes y datos; responde STOP cuando quieras.`,
  policiesLead: "Los textos y correos los envía waivers.tattoo en nombre del estudio. ",
  privacyLink: "Política de privacidad",
  policiesJoin: " · ",
  siteTermsLink: "Términos",

  // La perforación de un menor con su madre, padre o tutor presente (TW-0128).
  guardianSection: "Madre, padre o tutor",
  guardianWhy: (shop: string) =>
    `Eres menor de 18 años, así que tu madre, padre o tutor legal da el consentimiento contigo. Esta parte la llenan y la firman ellos, en persona, con ${shop}.`,
  guardianStateWants: (state: string) => `Lo que exige ${state}`,
  guardianShopWants: "Lo que exige este estudio",
  guardianName: "Nombre completo de tu madre, padre o tutor",
  guardianNamePlaceholder: "Su nombre completo",
  guardianRelationship: "Su parentesco contigo",
  guardianRelationshipPlaceholder: "Madre, padre, tutor legal",
  guardianSignature: "Firma de tu madre, padre o tutor",
  guardianIdPhoto: "Foto de la identificación de tu madre, padre o tutor",
  guardianIdNote: "Su identificación oficial con foto, no la tuya. El estudio guarda una copia en el expediente.",
  needGuardianName: "Escribe el nombre completo de tu madre, padre o tutor.",
  needGuardianRelationship: "Dinos su parentesco contigo.",
  needGuardianSignature: "Tu madre, padre o tutor debe firmar el formulario.",
  // TW-0267 — la declaración que la regla del estado fija palabra por palabra se muestra EN INGLÉS también aquí: la regla
  // fija las palabras. Solo esta línea de arriba y el rechazo van en español (esperan la lectura de un hablante nativo).
  guardianCertificationLead: (state: string) =>
    `${state} exige que la madre, el padre o el tutor firme esta declaración con estas palabras exactas, en inglés:`,
  guardianPresenceLead: (state: string) => `La norma de ${state} pide que la madre, el padre o el tutor confirme esto antes de firmar:`,
  // TW-0526 — la norma fija estas palabras en inglés y permite decirlas en sustancia; esta es nuestra versión en español (espera la
  // lectura de un hablante nativo)
  guardianTranslatedLead: (state: string) =>
    `La norma de ${state} pide que la madre, el padre o el tutor firme esta declaración; estas son sus palabras en español:`,
  needGuardianCertification: (state: string) => `Tu madre, padre o tutor debe marcar la declaración de ${state} antes de firmar.`,
  // TW-0279 — three-way, and no else states a law (the English above has why)
  minorStateNo: (state: string, service: string) =>
    service === "tattoo"
      ? `${state} no permite tatuajes a menores de 18 años, ni siquiera con el consentimiento de la madre, el padre o el tutor.`
      : service === "piercing"
        ? `${state} no permite perforaciones a menores de 18 años, ni siquiera con el consentimiento de la madre, el padre o el tutor.`
        : service === "cosmetic"
          ? "Debes tener 18 años para hacerte un tatuaje cosmético."
          : "Debes tener 18 años.",
  minorEarlobeNote: "Los lóbulos de la oreja suelen tratarse aparte: pregunta a tu perforador.",
  // TW-0225 — pendiente de la lectura de un hablante nativo, como el resto del español
  earAsk: (scope: string, method: string): string =>
    scope === "ear"
      ? method === "stud_gun"
        ? "¿Es una perforación en la oreja hecha con una pistola de arete y broche de un solo uso?"
        : "¿Es una perforación en la oreja?"
      : scope === "perimeter"
        ? method === "stud_gun"
          ? "¿Es una perforación en el lóbulo o en el borde exterior de la oreja (hélix), y no en el tragus, la concha u otro cartílago dentro del borde, hecha con una pistola de arete y broche de un solo uso?"
          : "¿Es una perforación en el lóbulo o en el borde exterior de la oreja (hélix), y no en el tragus, la concha u otro cartílago dentro del borde?"
        : method === "stud_gun"
          ? "¿Es una perforación en el lóbulo de la oreja, sin cartílago, hecha con una pistola de arete y broche de un solo uso?"
          : "¿Es una perforación en el lóbulo de la oreja, sin cartílago?",
  earNeedleIsNo: (scope: string): string =>
    scope === "ear"
      ? "Una aguja en la oreja es un no."
      : scope === "perimeter"
        ? "Una aguja en el lóbulo o en el borde es un no."
        : "Una aguja en el lóbulo es un no.",
  earOutside: (state: string) =>
    `La regla de ${state} para la perforación de un menor no alcanza a esta. Tu madre, padre o tutor igual da su consentimiento y firma.`,
  minorEarOutsideNote: (scope: string, method: string): string =>
    scope === "ear"
      ? method === "stud_gun"
        ? `Una oreja perforada con una pistola de arete y broche de un solo uso queda fuera de esta regla: elige "Lóbulo de la oreja" o "Cartílago de la oreja" como lugar, más abajo.`
        : `Una perforación en la oreja queda fuera de esta regla: elige "Lóbulo de la oreja" o "Cartílago de la oreja" como lugar, más abajo.`
      : scope === "perimeter"
        ? method === "stud_gun"
          ? `Un lóbulo o un borde exterior de la oreja perforado con una pistola de arete y broche de un solo uso queda fuera de esta regla: elige "Lóbulo de la oreja" o "Cartílago de la oreja" como lugar, más abajo.`
          : `Una perforación en el lóbulo o en el borde exterior de la oreja queda fuera de esta regla: elige "Lóbulo de la oreja" o "Cartílago de la oreja" como lugar, más abajo.`
        : method === "stud_gun"
          ? `Un lóbulo perforado con una pistola de arete y broche de un solo uso queda fuera de esta regla: elige "Lóbulo de la oreja" como lugar, más abajo.`
          : `Una perforación en el lóbulo de la oreja queda fuera de esta regla: elige "Lóbulo de la oreja" como lugar, más abajo.`,
  // TW-0595 — pendiente de la lectura de un hablante nativo, como el resto del español
  minorFloorEarlobeNote: `La perforación del lóbulo de la oreja es la excepción a esa edad, con la madre, el padre o el tutor y todo lo demás que pide la regla: elige "Lóbulo de la oreja" como lugar, más abajo.`,
  minorCoverupAsk: "¿Este tatuaje cubre un tatuaje que ya tienes?",
  minorCoverupYes: "Sí",
  minorCoverupNo: "No",
  minorCoverupOnly: (state: string) =>
    `${state} solo permite tatuar a menores de 18 años para cubrir un tatuaje que ya tienen.`,
  minorUnderFloor: (state: string, floor: string, service: string) =>
    service === "tattoo"
      ? `${state} no permite tatuajes a menores de ${floor} años, ni siquiera con el consentimiento de la madre, el padre o el tutor.`
      : service === "piercing"
        ? `${state} no permite perforaciones a menores de ${floor} años, ni siquiera con el consentimiento de la madre, el padre o el tutor.`
        : service === "cosmetic"
          ? "Debes tener 18 años para hacerte un tatuaje cosmético."
          : "Debes tener 18 años.",
  minorPlacementNo: (state: string, placement: string) =>
    `${state} no permite una perforación de ${placement.toLowerCase()} a menores de 18 años, firme quien firme. Elige otro lugar o vuelve a los 18.`,
  minorPlacementUnverifiable: (state: string) =>
    `${state} prohíbe algunas perforaciones a menores de 18 años, así que elige el lugar exacto en vez de "Otro lugar".`,
  minorCite: (cite: string) => `La regla: ${cite}`,
  minorCountyRule: (state: string) => `Esta es una regla local, más estricta que la de ${state}.`,
  minorEmancipatedAsk: "¿Eres un menor emancipado y traes una copia certificada de la orden judicial?",
  minorIdPhoto: "Foto de la identificación del menor",
  minorIdNote: "Tu estado pide la identificación con foto del menor además de la de la madre, el padre o el tutor. Vale una credencial escolar o un pasaporte si no tiene licencia.",
  guardianProofPhoto: "Foto del acta de nacimiento o de los papeles de tutela",
  guardianProofNote: "El acta de nacimiento del menor, o la orden de adopción, custodia o tutela: el documento que muestra que este adulto es su madre, padre o tutor legal.",
  needGuardianProofPhoto: "Agrega una foto del acta de nacimiento o de los papeles de tutela.",
  // TW-0270 slice 5 — el documento que la regla del estado guarda con el expediente de un menor, con el nombre en español que
  // lleva el registro del estado (`name_es`); pendiente de la lectura de un hablante nativo, como el resto
  paperPhoto: (paper: { name: string; name_es: string }) => `Foto del documento: ${paper.name_es}`,
  paperNote: (place: string) => `La regla de ${place} pide este documento. La foto se guarda con este formulario.`,
  needPaperPhoto: (paper: { name: string; name_es: string }) => `Agrega una foto del documento: ${paper.name_es}.`,
  // TW-0279 slice 10 — las instrucciones escritas del médico; pendiente de la lectura de un hablante nativo, como el resto
  providerInstructionsPhoto: "Foto de las instrucciones escritas de tu médico",
  providerInstructionsNote: "Las instrucciones que tu médico o asistente médico escribió para este trabajo.",
  providerInstructionsByShop: (shop: string) => `${shop} guarda las instrucciones escritas de tu médico para este trabajo con tu expediente.`,
  scalpScarsAsk: "¿Este trabajo en el cuero cabelludo cubre cicatrices, como las de un trasplante de cabello, una cirugía o una lesión?",
  needScalpScarsAnswer: "Responde sí o no: ¿este trabajo en el cuero cabelludo cubre cicatrices?",
  // TW-0227 — el permiso por escrito del otro padre o de la otra madre; pendiente de la lectura de un hablante nativo, como
  // el resto del español. La casilla es la frase de consent-sentences-es.json, la misma que imprime el formulario firmado.
  secondParentTitle: "El permiso por escrito del otro padre o de la otra madre",
  secondParentWhy: (who: string) => `${who} pide el permiso por escrito de los dos padres, o del tutor legal.`,
  secondParentWhyPresent: (who: string) => `${who} pide que los dos padres, o el tutor legal, estén aquí y firmen.`,
  secondParentSigns: "El otro padre o la otra madre firma aquí",
  secondParentPhotoOption: "Una foto del permiso firmado por el otro padre o la otra madre",
  soleParent: consentEs.secondParent.soleParent,
  secondParentName: "Nombre completo del otro padre o de la otra madre",
  secondParentNamePlaceholder: "Su nombre completo",
  secondParentSignature: "Firma del otro padre o de la otra madre",
  secondParentPhoto: "Foto del permiso firmado por el otro padre o la otra madre",
  secondParentPhotoNote: "La nota que firmó el otro padre o la otra madre para dar su permiso. Que salga la hoja completa y se pueda leer.",
  needSecondParentChoice: "Elige cómo da su permiso por escrito el otro padre o la otra madre.",
  needSecondParentName: "Escribe el nombre completo del otro padre o de la otra madre.",
  needSecondParentSignature: "El otro padre o la otra madre debe firmar el formulario.",
  needSecondParentPhoto: "Agrega una foto del permiso firmado por el otro padre o la otra madre.",
  minorEmancipatedNote: (state: string) =>
    `${state} trata a un menor emancipado como a un adulto, así que firmas tú. Trae la orden certificada: el estudio tiene que verla.`,

  signedDate: "Fecha de firma",
  // TW-0488 (2026-10-26). Awaiting a native-speaker read like the rest of the ES copy in this file.
  idPhoto: "Foto de tu identificación",
  idPhotoNote: "Licencia de conducir, identificación estatal, pasaporte u otra identificación oficial con foto.",
  idTypeQuestion: "¿Qué identificación es?",
  idTypeNames: {
    drivers_license: "Licencia de conducir",
    state_id: "Identificación estatal",
    passport: "Pasaporte",
    other: "Otra identificación oficial",
  },
  idNumber: "Número de identificación",
  idNumberPlaceholder: {
    drivers_license: "El número de tu licencia",
    state_id: "El número de tu identificación",
    passport: "El número de la página de la foto de tu pasaporte",
    other: "El número de tu identificación",
    "": "El número de tu identificación",
  },
  idNumberWhy: (state: string) => `La regla de ${state} guarda en el registro el número de esta identificación. Escríbelo tal como aparece.`,
  takePhoto: "Tomar foto",
  chooseFromLibrary: "Elegir de tus fotos",
  openingPhoto: "Abriendo la foto...",
  savedPhotoUnreadable: "No pudimos abrir esa foto. Prueba con otra o toma una foto.",
  savedPhotoTooBig: "Esa foto es demasiado grande. Prueba con otra o toma una foto.",
  retakePhoto: "Volver a tomar",
  clearPhoto: "Quitar foto",
  retake: "Repetir",
  usePhoto: "Usar foto",
  cameraShutter: "Tomar la foto",
  cameraClose: "Cerrar la cámara",
  cameraBlocked: "La cámara no se abrió aquí. Puedes tomar la foto con la cámara de tu teléfono.",
  cameraApp: "Abrir la cámara de mi teléfono",
  cameraPreview: "La foto que acabas de tomar",
  clientSignature: "Firma del cliente",
  signHere: "Firma aquí",
  clear: "Borrar",
  needleBatch: "Lote de agujas",
  lotPhotoRequired: "Obligatoria",
  inkBatch: "Lote de tinta",
  pigmentBatch: "Lote de pigmento",
  submit: "Enviar",
  submitting: "Enviando...",

  invalidClient: "Cliente no válido",
  tellUsService: (works: string | string[]) => `Dinos si hoy es ${orList(works, "o")}.`,
  // TW-0541 — the browser's own stop, in the page's words (a companion's Spanish, awaiting a native read)
  aboveStillEmpty: "Arriba hay una casilla vacía. Sube hasta la marcada en rojo.",
  aboveNeedsAFix: "Arriba hay una casilla por corregir. Sube hasta la marcada en rojo.",
  pickOneHere: "Elige una opción de la lista.",
  fillThisIn: "Rellena esto para firmar.",
  fixThisBox: "Corrige esto para firmar.",
  noInfluence: "No puedes estar bajo los efectos de drogas o alcohol.",
  noPregnant: "No puedes estar embarazada ni amamantando.",
  needCommunicableDetails: "Debes indicar cuál es tu enfermedad contagiosa.",
  // 2026-10-01. Awaiting a native-speaker read like the rest of the ES copy in this file.
  needHealthAnswer: (question: string) => `Responde sí o no: ${question}`,
  // 2026-10-01. Awaiting a native-speaker read like the rest of the ES copy in this file.
  needHealthDetails: (question: string) => `Agrega los detalles de tu sí: ${question}`,
  needTerms: "Debes aceptar los términos y condiciones para firmar.",
  tickThisBox: "Marca esta casilla para firmar.",
  // 2026-10-02. Awaiting a native-speaker read like the rest of the ES copy in this file.
  needCashPreferred: "Debes marcar la casilla sobre el pago con tarjeta para firmar.",
  // 2026-10-08 — the two HIPAA sentences the API prints too are consent-sentences-es.json's (`hipaa`), the one source; the
  // lead is the form's own. Awaiting a native-speaker read like the rest of the ES copy in this file.
  privacyNoticeLead: "Lee el aviso de este consultorio y luego marca la casilla de abajo.",
  privacyNoticeAck: consentEs.hipaa.privacyNoticeAck,
  needPrivacyNotice: consentEs.hipaa.privacyNoticeRefused,
  photoReleaseHeading: consentEs.hipaa.photoRelease.heading,
  photoReleaseLines: consentEs.hipaa.photoRelease.lines,
  photoReleaseBox: consentEs.hipaa.photoRelease.box,
  yearsWords: (n: number) => (n === 1 ? "1 año" : `${n} años`),
  // 2026-09-23. Awaiting a native-speaker read like the rest of the ES copy in this file.
  needPaymentMethod: "Dinos cómo pagas hoy.",
  // TW-0488. Awaiting a native-speaker read like the rest of the ES copy in this file.
  needIdType: "Dinos qué identificación es.",
  needIdNumber: "Escribe el número de tu identificación.",
  needIdPhoto: "Debes tomar una foto de tu identificación.",
  needSignature: "Debes firmar el formulario.",
  needNeedlePhoto: "Debes tomar una foto del número de lote de las agujas.",
  needInkPhoto: "Debes tomar una foto del número de lote de la tinta.",
  submitError: (code: number) => `Hubo un error al enviar el formulario (${code}).`,
  submitOffline: "Tu formulario no se envió. Revisa tu conexión y vuelve a tocar Enviar. Todo lo que llenaste sigue aquí.",
  stateRulesLoading: "Un momento: el resto de este formulario todavía se está cargando. Vuelve a tocar Enviar en unos segundos.",
  stateRulesFailed: "Una parte de este formulario no se cargó. Revisa tu conexión y toca Intentar de nuevo. Todo lo que llenaste sigue aquí.",
  stateRulesTryAgain: "Intentar de nuevo",
  invalidBirthDate: "Ingresa una fecha de nacimiento válida.",

  success: "Firmado.",
  submitted: (shop: string) => (shop.trim() ? `${shop.trim()} tiene tu formulario firmado.` : "El estudio tiene tu formulario firmado."),
  ownerDone: "¿Eres el dueño y estás probando tu formulario? Este ya está en tu correo y en tu panel.",
  backToQr: "Volver a tu código QR",
  download: "Descargar tu formulario",
  submitAnother: "Enviar otro formulario",
  // 2026-10-08. Awaiting a native-speaker read like the rest of the ES copy in this file.
  copyHeading: (shop: string) => `Tu copia de lo que firmaste en ${shop}`,
  copyShopPrints: (shop: string) => `${shop} también te va a imprimir una copia.`,
  copyPrint: "Imprimir o guardar",
  copyReleaseDeclined: "No firmaste la autorización para usar fotos.",
  copyNotAnswered: "Sin respuesta",
  copyNone: "Nada",

  lockedTitle: (shop: string) => `${shop} necesita activar su página de formularios`,
  lockedBody: (price: string) =>
    `Los formularios gratuitos de esta página se agotaron. Si administras el estudio: toca abajo para activarla. ${price} al año, formularios ilimitados. Esta página vuelve a funcionar en cuanto se complete.`,
  activate: (shop: string) => `Activar ${shop}`,
  notTheOwner: "¿No eres el dueño? Dale este teléfono a alguien que trabaje aquí.",

  // TW-0517. Awaiting a native-speaker read like the rest of the ES copy in this file.
  coldBanner: (shop: string) => `Esta es la página de formularios de ${shop}. Ya está activa.`,
  coldAddEmail: "Agrega tu correo",
  coldAddLogo: "Agrega tu logo",
  coldDismiss: "Cerrar",
  coldPay: (price: string) => `Pagar ${price} al año`,
  coldSetUp: "Configura tu cuenta",

  // TW-0286. Awaiting a native-speaker read like the rest of the ES copy in this file.
  topUpLine: (n: number) => `¿Trabajas aquí pero no lo administras? Consigue ${plural(n, 'formulario más', 'formularios más')} mientras el estudio decide.`,
  topUpEmailLabel: "Tu correo",
  topUpButton: (n: number) => `Conseguir ${plural(n, 'formulario más', 'formularios más')}`,
  topUpBusy: (n: number) => `Agregando ${n} más…`,
  topUpNote: "Le escribiremos al dueño que lo pediste, con copia para ti.",
  topUpOwnerEmail: "Ese es el correo del estudio, así que toca Activar arriba.",
  topUpAlready: (n: number) => `Esta página ya recibió sus ${plural(n, 'formulario más', 'formularios más')}. Quien administra el estudio puede activarla arriba.`,
  topUpBadEmail: "Ese correo no se ve bien.",
  topUpNotOpen: "Esta página no puede recibir más formularios. Quien administra el estudio puede activarla arriba.",
  topUpTryAgain: "No pudimos agregarlos ahora. Inténtalo de nuevo en un minuto.",

  // TW-0158. Awaiting a native-speaker read like the rest of the ES copy in this file.
  overTrialNote: (lock: number) =>
    `Para el estudio: se agotaron los formularios gratuitos. Esta página se pausa a los ${lock}.`,
  overTrialAction: (price: string) => `Activar por ${price} al año`,

  noShopTitle: "No hay ningún estudio en esta dirección.",
  noShopBody: "Revisa el enlace o escanea el código QR del estudio.",
  // TW-0593. Awaiting a native-speaker read like the rest of the ES copy in this file.
  closedTitle: "El formulario de este estudio está cerrado.",
  closedBody: "Pregúntale al estudio cómo quiere que firmes.",
  loadFailedTitle: "No pudimos cargar esta página.",
  loadFailedBody: "Inténtalo de nuevo en un momento. Si sigue pasando, avísale al estudio.",

  crashTitle: "Algo salió mal en esta página.",
  crashBody: "Recarga la página e inténtalo de nuevo. Si sigue pasando, avísale al estudio.",
};

export const strings = (lang: Lang): Strings => (lang === "es" ? ES : EN);

/** Tell the DOCUMENT which language it is in (TW-0094). `<html lang>` is baked to "en" at build
 *  time in public/index.html while the language is runtime state, so the two only agree by
 *  accident unless a surface says so — and until this existed, no surface did.
 *
 *  It is not cosmetic on a consent form. `lang` is the attribute a screen reader reads to choose
 *  pronunciation rules, so a Spanish sentence under `lang="en"` is announced with English
 *  phonetics: a client who cannot read the screen has the thing they are signing mispronounced to
 *  them, which is the exact failure translating the form was meant to prevent. It also decides
 *  whether a browser offers to translate the page, and how words hyphenate.
 *
 *  Both surfaces call it — the form, which follows the switch live, and the consent page, which
 *  takes its language off the URL. Safe where there is no document (a server render, a bare test). */
export function setDocumentLang(lang: Lang): void {
  if (typeof document === "undefined") return;
  document.documentElement.lang = lang;
}

/** The posted body, with the language the client signed in — ONLY when it was not English. An
 *  English signing gets back the very object it handed in, not a copy: a tattoo shop's body is
 *  byte-for-byte what it has posted since 2023 (the same rule artists.ts's withArtistPicked keeps),
 *  and the API prints the Spanish sentences on the PDF only when this key says so. */
export function withLanguage<T extends Record<string, any>>(body: T, lang: Lang): Record<string, any> {
  return lang === "es" ? { ...body, language: "es" } : body;
}

/** The consent page's address for this language — the checkbox's link opens it in a new tab, which
 *  knows nothing of the form's switch, so the language rides the URL. */
export const termsHref = (lang: Lang): string =>
  lang === "es" ? "/terms-and-conditions?lang=es" : "/terms-and-conditions";

/** Every key, so a test can prove both tables carry the same shape and no Spanish string is a
 *  stray English one (a missed line reads to a Spanish speaker as a broken page). */
export const STRING_KEYS = Object.keys(EN) as (keyof Strings)[];
